import { useEffect, useState } from 'react';
import {
  isLaunchpadSplashSeen,
  markLaunchpadSplashSeen,
} from 'utils/launchpadSplash';

export { LAUNCHPAD_HUB_SPLASH_SEEN_KEY } from 'utils/launchpadSplash';

/** MUST NOT reduce without spec update — see docs/mockups/launchpad/launchpad-instructions.md §13 */
export const SPLASH_FADE_MS = 2000;

export const SPLASH_HOLD_MS = 1000;

type TSplashPhase = 'visible' | 'fading' | 'hidden';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getInitialSplashPhase(): TSplashPhase {
  return isLaunchpadSplashSeen() ? 'hidden' : 'visible';
}

export default function useLaunchpadSplash(): {
  splashPhase: TSplashPhase,
} {
  const [splashPhase, setSplashPhase] = useState<TSplashPhase>(getInitialSplashPhase);

  useEffect(() => {
    if (isLaunchpadSplashSeen()) {
      return undefined;
    }

    const fadeMs = prefersReducedMotion() ? 0 : SPLASH_FADE_MS;

    function completeSplash() {
      markLaunchpadSplashSeen();
      setSplashPhase('hidden');
    }

    function startSplashFade() {
      setTimeout(() => {
        if (fadeMs === 0) {
          completeSplash();
          return;
        }

        setSplashPhase('fading');
        setTimeout(completeSplash, fadeMs);
      }, SPLASH_HOLD_MS);
    }

    if (document.readyState === 'complete') {
      startSplashFade();
    } else {
      window.addEventListener('load', startSplashFade, { once: true });
    }

    return undefined;
  }, []);

  return { splashPhase };
}
